import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { addMinutes, endOfDay, startOfDay } from 'date-fns/esm';
import { useTranslation } from 'react-i18next';

import { Actions } from '../../../../core/state/bookings/actions';
import { BookingModalType } from '../../../../core/state/bookings/types';
import { differenceInMinutesToCurrentTime, formatDate, getTimeDate } from '../../../../core/utils/date';
import { DATE_FORMAT_HOURS_MINUTES, DATE_FORMAT_ISO } from '../../../../core/constants/date';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { useInternalUser } from '../../../../core/hooks/useInternalUser';
import { isReadAllRole } from '../../../../core/state/user/selectors';
import { getFilters, getSelectedProjectId } from '../../../../core/state/bookings/selectors';

import { TimeSlotDefinition } from '../../types';
import Tooltip from '../../../../components/tooltip/Tooltip';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import Button from '../../../../components/form/button/Button';
import { Color } from '../../../../core/enums/Color';
import { RootState } from '../../../../core/state/state';

import './TimeSlot.scss';

interface Props {
  date: Date;
  config: TimeSlotDefinition;
  isBlocked: boolean;
  isPartiallyBlocked: string;
  overflowRows: number[];
}

const TimeSlot: React.FC<Props> = ({ date, config, isBlocked, isPartiallyBlocked, overflowRows }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isInternal = useInternalUser();
  const { index, rowSpan, startTime, onHour } = config;
  const isUserReader = useSelector(isReadAllRole);
  const isMobileScreen = isMobileScreenSize();
  const projectId = useSelector(getSelectedProjectId);
  const filters = useSelector(getFilters);
  const calendarSelectedDate = useSelector((state: RootState) => state.bookings.selectedDate);

  const createBooking = useCallback(
    (exclude: boolean) => {
      if (isBlocked && !exclude) {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Error,
            title: t('booking.slot.blocked'),
            message: t('booking.slot.blocked.message')
          })
        );
        return;
      }
      const endTimeDate =
        isPartiallyBlocked === 'none' ? addMinutes(getTimeDate(startTime), onHour ? 60 : 30) : addMinutes(getTimeDate(startTime), 30);
      const endTime = formatDate(endTimeDate, DATE_FORMAT_HOURS_MINUTES);

      const initialValues = {
        date: formatDate(date, DATE_FORMAT_ISO),
        startTime,
        endTime
      };
      dispatch(
        Actions.openModal({
          type: isInternal ? BookingModalType.Internal : BookingModalType.LogisticPlan,
          initialValues
        })
      );
    },
    [date, dispatch, isBlocked, onHour, startTime, t, isInternal, isPartiallyBlocked]
  );

  const openListView = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!projectId) return;
      const selectedDay = startOfDay(date);
      dispatch(Actions.changeDate(selectedDay));
      dispatch(
        Actions.changeFilters({
          ...filters,
          dateRange: {
            start: selectedDay,
            end: endOfDay(selectedDay)
          }
        })
      );
      history.push({
        pathname: `/projects/${projectId}/bookings/list`,
        state: {
          selectedDate: formatDate(selectedDay, DATE_FORMAT_ISO),
          restore: {
            selectedDate: calendarSelectedDate
          }
        }
      });
    },

    [calendarSelectedDate, date, dispatch, filters, history, projectId]
  );

  let highlightHour = false;
  const timeDifference = differenceInMinutesToCurrentTime(startTime);
  if (onHour && Math.abs(timeDifference) <= 30) {
    highlightHour = !(Math.abs(timeDifference) === 30 && timeDifference > 0);
  }

  const topOverflowCount = overflowRows[index - 1] ?? 0;
  const bottomOverflowCount = rowSpan === 2 ? overflowRows[index] ?? 0 : 0;
  const showTopOverflow = topOverflowCount > 0;
  const showBottomOverflow = bottomOverflowCount > 0 && !showTopOverflow;

  return (
    <>
      {config.rowSpan === 2 && isPartiallyBlocked !== 'none' ? (
        <>
          {['top', 'bottom'].map((data, i) => {
            return (
              <button
                key={data}
                className={`slot ${highlightHour ? 'slot--highlight' : ''} ${isMobileScreen && 'slot--mobile'} ${
                  isPartiallyBlocked === data ? 'slot--blocked' : ''
                }`}
                onClick={isUserReader ? () => {} : () => createBooking(isPartiallyBlocked !== data)}
                data-slot={index}
                data-span={rowSpan}
                style={{ gridRow: `${index + i} / span ${rowSpan - 1}` }}
                aria-label={t('booking.create.heading')}
              >
                {highlightHour && <span className="slot__highlight" />}
                {!isUserReader && (
                  <Tooltip placement="top" text={t(isPartiallyBlocked === data ? 'booking.slot.blocked' : 'booking.create.heading')} />
                )}
                {topOverflowCount > 0 && isPartiallyBlocked !== data && (
                  <div className="slot__overflow">
                    <Tooltip placement="right" text={t('booking.viewAllBookings')}>
                      <Button buttonTypes={['xx-small', 'blue']} color={Color.Blue} onClick={openListView}>
                        +{topOverflowCount}
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </button>
            );
          })}
        </>
      ) : (
        <button
          className={`slot ${highlightHour ? 'slot--highlight' : ''} ${isMobileScreen && 'slot--mobile'} ${isBlocked ? 'slot--blocked' : ''}`}
          onClick={isUserReader ? () => {} : () => createBooking(false)}
          data-slot={index}
          data-span={rowSpan}
          style={{ gridRow: `${index} / span ${rowSpan}` }}
          aria-label={t('booking.create.heading')}
        >
          {highlightHour && <span className="slot__highlight" />}
          {!isUserReader && <Tooltip placement="top" text={t(isBlocked ? 'booking.slot.blocked' : 'booking.create.heading')} />}
          {(showTopOverflow || showBottomOverflow) && (
            <>
              {showTopOverflow && (
                <div className="slot__overflow slot__overflow--top">
                  <Tooltip placement="right" text={t('booking.viewAllBookings')}>
                    <Button buttonTypes={['xx-small', 'blue']} color={Color.Blue} onClick={openListView}>
                      +{topOverflowCount}
                    </Button>
                  </Tooltip>
                </div>
              )}

              {showBottomOverflow && (
                <div className="slot__overflow slot__overflow--bottom">
                  <Tooltip placement="right" text={t('booking.viewAllBookings')}>
                    <Button buttonTypes={['xx-small', 'blue']} color={Color.Blue} onClick={openListView}>
                      +{bottomOverflowCount}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </>
          )}
        </button>
      )}
    </>
  );
};
export default TimeSlot;
